import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import * as moment from 'moment';
import { IAlbum, Lightbox } from 'ngx-lightbox';
import { Observable } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import DateHelper from 'src/app/helpers/date-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import Employee from 'src/app/model/product/employee';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import {
  AssignSubProjectResult,
  AssignSubProjectToTourControllerRequestService,
} from '../../service/assign-sub-project-to-tour-controller-request.service';
import {
  AssignImmoTaskResult,
  AssignImmoTaskToTourControllerRequestService,
} from '../../service/assign-immo-task-to-tour-controller-request.service';
import { TourDateChangeService } from '../../service/tour-date-change.service';
import { TourPlanningEventService } from '../../service/tour-planning-event.service';
import {
  ImmoMapHelper,
  LastSelectedPlanValues,
  PreviewMarkerSelectedEvent,
} from '../helper/immo-map-helper';
import {
  ImmoMapControllerRequestService,
  SingleImmoTaskForMapResponse,
  SingleSubProjectForMapResponse,
} from '../services/immo-map-controller-request.service';
import {
  ImmoMapImmoTask,
  ImmoMapItem,
  ImmoMapSubProject,
} from '../services/model/immo-map-sub-project';
import { ImmoMapPopupInfoHelper } from '../immo-map-popup/immo-map-popup-info/helper/immo-map-popup-info-helper';
import Asset from 'src/app/model/asset';
import {
  ModalService,
  ObservableHandlerPair,
} from 'src/app/services/modal.service';
import { CreateEditTaskModalComponent } from 'src/app/pages/immo-ip/task/create-edit-task/create-edit-task-modal.component';
import { TourPreviewStateService } from '../../service/tour-preview-state.service';

@Component({
  selector: 'preview-marker-popup',
  templateUrl: './preview-marker-popup.component.html',
  styleUrls: ['./preview-marker-popup.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: DateHelper.DEFAULT_MOMENT_FORMATS,
    },
  ],
})
export class PreviewMarkerPopupComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @Input()
  subProjectId: number;

  @Input()
  immoTaskId: number;

  @Input()
  placerOptions: Employee[];

  @Input()
  previewMarkerSelected: Observable<PreviewMarkerSelectedEvent>;

  @Output()
  updatePlanValues = new EventEmitter<LastSelectedPlanValues>();

  @Output()
  itemReplanned = new EventEmitter<ImmoMapItem>();

  init: boolean = false;
  submitting: boolean = false;

  subProject: ImmoMapSubProject;
  involvedCompanies: string[];

  immoTask: ImmoMapImmoTask;

  selectedPlacer: Employee;
  selectedTourDate: moment.Moment;
  locked: boolean = false;

  tourDateHasChanged: boolean = false;

  today = new Date();

  tourPlacerId: number;
  tourDate: Date;

  constructor(
    private immoMapControllerRequestService: ImmoMapControllerRequestService,
    private assignSubProjectToTourControllerRequestService: AssignSubProjectToTourControllerRequestService,
    private assignImmoTaskToTourControllerRequestService: AssignImmoTaskToTourControllerRequestService,
    private tourDateChangeService: TourDateChangeService,
    private tourPlanningEventService: TourPlanningEventService,
    private modalService: ModalService,
    private tourPreviewStateService: TourPreviewStateService,
    private _lightbox: Lightbox
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.subProjectId != null) {
      this.initializeSubProject();
    } else if (this.immoTaskId != null) {
      this.initializeImmoTask();
    }

    this.addSubscription(
      this.tourDateChangeService.getTourDateSubject(),
      (date: moment.Moment) => {
        if (!this.tourDateHasChanged) {
          this.selectedTourDate = date;
        }
      }
    );

    // Check for last activated preview installer and prefill if available
    const lastActivatedPreviewInstaller =
      this.tourPreviewStateService.getLastActivatedPreviewInstaller();
    if (lastActivatedPreviewInstaller && !this.selectedPlacer) {
      this.selectedPlacer = lastActivatedPreviewInstaller;
    }

    // Subscribe to preview marker selection for prefilling installer
    this.addSubscription(
      this.previewMarkerSelected,
      (event: PreviewMarkerSelectedEvent) => {
        if (
          event.lastSelectedPlanValues != null &&
          (event.subProjectId === this.subProjectId ||
            event.immoTaskId === this.immoTaskId)
        ) {
          this.fillPlanValues(event.lastSelectedPlanValues);
        }
      }
    );
  }

  private initializeSubProject(): void {
    this.immoMapControllerRequestService
      .singleSubProjectForMap(this.subProjectId)
      .then((res: SingleSubProjectForMapResponse) =>
        this.doInitializeSubProject(res)
      );
  }

  private doInitializeSubProject(res: SingleSubProjectForMapResponse): void {
    ImmoMapHelper.processSubProjectAppointmentInfo(res.subProject);
    ImmoMapPopupInfoHelper.setFirstAssetStyle(res.subProject);
    ImmoMapPopupInfoHelper.setTypeDisplays(res.subProject);
    if (res.subProject.project != null) {
      this.involvedCompanies = ImmoMapPopupInfoHelper.getInvolvedCompanies(
        res.subProject.project
      );
      ImmoMapPopupInfoHelper.preprocessProducts(
        res.subProject.project.products
      );
    }

    this.subProject = res.subProject;
    this.locked = res.locked;
    this.tourDate = res.tourDate;
    this.tourPlacerId = res.placerId;
    this.init = true;
  }

  private initializeImmoTask(): void {
    this.immoMapControllerRequestService
      .singleImmoTaskForMap(this.immoTaskId)
      .then((res: SingleImmoTaskForMapResponse) =>
        this.doInitializeImmoTask(res)
      );
  }

  private doInitializeImmoTask(res: SingleImmoTaskForMapResponse): void {
    ImmoMapPopupInfoHelper.setFirstAssetStyle(res.immoTask);

    this.immoTask = res.immoTask;
    this.locked = res.locked;
    this.tourDate = res.tourDate;
    this.tourPlacerId = res.placerId;
    this.init = true;
  }

  openTaskEditModal(): void {
    this.modalService.openComponentInModal(
      CreateEditTaskModalComponent,
      [
        {
          key: 'taskId',
          value: this.immoTask.id,
        },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.result,
        (result) => {
          if (result === 'taskCreatedOrEdited' || result === 'taskArchived') {
            this.itemReplanned.next();
            this.tourPlanningEventService
              .getTourChangedForPlacersSubject()
              .next([
                {
                  placerId: this.immoTask.placerId,
                  date: this.immoTask.tourDate,
                },
              ]);
          }
        }
      ),
      { minWidth: '97vw', height: '95vh' }
    );
  }

  replanItem(unassign: boolean): void {
    if (this.subProject != null) {
      this.doReplanSubProject(unassign);
    } else if (this.immoTask != null) {
      this.doReplanImmoTask(unassign);
    }
  }

  private doReplanSubProject(unassign: boolean): void {
    this.submitting = true;
    this.assignSubProjectToTourControllerRequestService
      .assignSubProjectToTour(
        this.subProject.id,
        unassign ? null : this.selectedPlacer.id,
        unassign ? null : this.selectedTourDate,
        unassign ? null : this.locked,
        unassign ? null : this.subProject.type
      )
      .then((result: AssignSubProjectResult) => {
        this.itemReplanned.emit(unassign ? this.subProject : null);
        this.tourPlanningEventService
          .getTourChangedForPlacersSubject()
          .next(result.affectedTours);
        if (!unassign) {
          this.updatePlanValues.emit({
            selectedPlacer: this.selectedPlacer,
          });
        }
      });
  }

  private doReplanImmoTask(unassign: boolean): void {
    this.submitting = true;
    this.assignImmoTaskToTourControllerRequestService
      .assignImmoTaskToTour(
        this.immoTask.id,
        unassign ? null : this.selectedPlacer.id,
        unassign ? null : this.selectedTourDate,
        unassign ? null : this.locked
      )
      .then((result: AssignImmoTaskResult) => {
        this.itemReplanned.emit(unassign ? this.immoTask : null);
        this.tourPlanningEventService
          .getTourChangedForPlacersSubject()
          .next(result.affectedTours);
        if (!unassign) {
          this.updatePlanValues.emit({
            selectedPlacer: this.selectedPlacer,
          });
        }
      });
  }

  updateLock(): void {
    const lockedVal = !this.locked;
    if (this.subProject != null) {
      this.assignSubProjectToTourControllerRequestService
        .assignSubProjectToTour(
          this.subProject.id,
          this.tourPlacerId,
          moment(this.tourDate),
          lockedVal,
          this.subProject.type
        )
        .then((result: AssignSubProjectResult) => {
          this.locked = lockedVal;
        });
    } else if (this.immoTask != null) {
      this.assignImmoTaskToTourControllerRequestService
        .assignImmoTaskToTour(
          this.immoTask.id,
          this.tourPlacerId,
          moment(this.tourDate),
          lockedVal
        )
        .then((result: AssignImmoTaskResult) => {
          this.locked = lockedVal;
        });
    }
  }

  selectedTourDateChanged(): void {
    this.tourDateHasChanged = true;
  }

  fillPlanValues(lastSelectedPlanValues: LastSelectedPlanValues): void {
    this.selectedPlacer = lastSelectedPlanValues.selectedPlacer;
    this.tourDateHasChanged = true;
  }

  openLightbox(): void {
    this.doOpenLightbox(
      this.subProject != null
        ? this.subProject.assets
        : this.immoTask != null
        ? this.immoTask.assets
        : null
    );
  }

  private doOpenLightbox(assets: Asset[]): void {
    if (CollectionHelper.isPopulated(assets)) {
      const album: IAlbum[] = assets.map((a) => {
        return {
          src: `/api/assets/${a.uuid}`,
          thumb: `/api/assets/${a.uuid}`,
          caption: '',
        };
      });
      this._lightbox.open(album, 0);
    }
  }

  compareEntities(o1: any, o2: any): boolean {
    return GeneralHelper.compareEntities(o1, o2);
  }
}
